import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import { motion, AnimatePresence } from 'framer-motion';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import { Play, Stop, MapPin, Lightning, Timer, Path } from '@phosphor-icons/react';
import { toast } from 'sonner';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import Speedometer from '../components/Speedometer';
import RideComplete from '../components/RideComplete';

import { VEHICLE_TYPES } from '../lib/vehicleTypes';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_TOKEN || 'pk.eyJ1IjoibWFwYm94IiwiYSI6ImNpejY4NXVycTA2emYycXBndHRqcmZ3N3gifQ.rJcFIG214AriISLbB6B5aw';

const VEHICLES = VEHICLE_TYPES.map(v => ({ id: v.id, name: v.label }));

export default function RideTracking() {
    const { user, updateUser } = useAuth();
    const [isRiding, setIsRiding] = useState(false);
    const [currentRide, setCurrentRide] = useState(null);
    const [vehicleType, setVehicleType] = useState(user?.vehicle_type || 'ebike');
    const [distance, setDistance] = useState(0);
    const [duration, setDuration] = useState(0);
    const [gpsTrace, setGpsTrace] = useState([]);
    const [lastPosition, setLastPosition] = useState(null);
    const [rideResult, setRideResult] = useState(null);
    const [mapLoaded, setMapLoaded] = useState(false);
    const [currentSpeed, setCurrentSpeed] = useState(0);
    const [showCelebration, setShowCelebration] = useState(false);
    
    const mapContainer = useRef(null);
    const map = useRef(null);
    const watchIdRef = useRef(null);
    const timerRef = useRef(null);
    const startTimeRef = useRef(null);
    const markerRef = useRef(null);

    // Initialize map
    useEffect(() => {
        if (map.current) return;

        // Get user location first
        navigator.geolocation.getCurrentPosition(
            (position) => {
                const { latitude, longitude } = position.coords;
                
                map.current = new mapboxgl.Map({
                    container: mapContainer.current,
                    style: 'mapbox://styles/mapbox/dark-v11',
                    center: [longitude, latitude],
                    zoom: 15,
                    attributionControl: false
                });

                map.current.addControl(new mapboxgl.NavigationControl(), 'top-right');

                map.current.on('load', () => {
                    setMapLoaded(true);
                    
                    // Add GPS trace source
                    map.current.addSource('gps-trace', {
                        type: 'geojson',
                        data: {
                            type: 'Feature',
                            geometry: {
                                type: 'LineString',
                                coordinates: []
                            }
                        }
                    });

                    // Add trace line layer
                    map.current.addLayer({
                        id: 'gps-trace-line',
                        type: 'line',
                        source: 'gps-trace',
                        layout: {
                            'line-join': 'round',
                            'line-cap': 'round'
                        },
                        paint: {
                            'line-color': '#1E90FF',
                            'line-width': 5,
                            'line-opacity': 0.9
                        }
                    });

                    // Add user marker
                    const el = document.createElement('div');
                    el.className = 'user-marker';
                    el.style.width = '20px';
                    el.style.height = '20px';
                    el.style.backgroundColor = '#1E90FF';
                    el.style.border = '3px solid #0a0a0f';
                    el.style.borderRadius = '50%';
                    el.style.boxShadow = '0 0 15px rgba(30, 144, 255, 0.6)';

                    markerRef.current = new mapboxgl.Marker(el)
                        .setLngLat([longitude, latitude])
                        .addTo(map.current);

                    setLastPosition({ lat: latitude, lng: longitude });
                });
            },
            (error) => {
                // Default to Toronto
                map.current = new mapboxgl.Map({
                    container: mapContainer.current,
                    style: 'mapbox://styles/mapbox/dark-v11',
                    center: [-79.3832, 43.6532],
                    zoom: 13,
                    attributionControl: false
                });
                map.current.on('load', () => setMapLoaded(true));
            }
        );

        return () => {
            if (map.current) map.current.remove();
            if (watchIdRef.current) navigator.geolocation.clearWatch(watchIdRef.current);
            if (timerRef.current) clearInterval(timerRef.current);
        };
    }, []);

    // Check for ongoing ride
    useEffect(() => {
        const checkOngoingRide = async () => {
            try {
                const res = await fetch(`${API}/rides/current`, { credentials: 'include' });
                if (res.ok) {
                    const ride = await res.json();
                    if (ride) {
                        setCurrentRide(ride);
                        setIsRiding(true);
                        setDistance(ride.distance || 0);
                        startTimeRef.current = new Date(ride.start_time);
                        startTracking();
                    }
                }
            } catch (error) {
                console.error('Check ongoing ride error:', error);
            }
        };
        checkOngoingRide();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const calculateDistance = (lat1, lon1, lat2, lon2) => {
        const R = 6371;
        const dLat = (lat2 - lat1) * Math.PI / 180;
        const dLon = (lon2 - lon1) * Math.PI / 180;
        const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
                  Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
                  Math.sin(dLon/2) * Math.sin(dLon/2);
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
        return R * c;
    };

    const startTracking = useCallback(() => {
        // Start timer
        timerRef.current = setInterval(() => {
            if (startTimeRef.current) {
                const elapsed = Math.floor((Date.now() - startTimeRef.current.getTime()) / 1000);
                setDuration(elapsed);
            }
        }, 1000);

        // Start GPS tracking
        if (navigator.geolocation) {
            watchIdRef.current = navigator.geolocation.watchPosition(
                (position) => {
                    const { latitude, longitude, speed: gpsSpeed } = position.coords;
                    const newPoint = { lat: latitude, lng: longitude, timestamp: new Date().toISOString() };

                    // Update speed from GPS (m/s -> km/h), fallback to distance-based calc
                    if (gpsSpeed !== null && gpsSpeed >= 0) {
                        setCurrentSpeed(gpsSpeed * 3.6);
                    }

                    setGpsTrace(prev => {
                        const updated = [...prev, newPoint];
                        
                        // Update map trace
                        if (map.current && mapLoaded) {
                            const coordinates = updated.map(p => [p.lng, p.lat]);
                            const source = map.current.getSource('gps-trace');
                            if (source) {
                                source.setData({
                                    type: 'Feature',
                                    geometry: { type: 'LineString', coordinates }
                                });
                            }
                        }
                        
                        return updated;
                    });

                    // Update marker position
                    if (markerRef.current) {
                        markerRef.current.setLngLat([longitude, latitude]);
                    }

                    // Pan map to follow
                    if (map.current) {
                        map.current.easeTo({ center: [longitude, latitude], duration: 500 });
                    }

                    setLastPosition(prev => {
                        if (prev) {
                            const delta = calculateDistance(prev.lat, prev.lng, latitude, longitude);
                            if (delta > 0.001 && delta < 1) {
                                setDistance(d => d + delta);
                                // Fallback speed calc if GPS speed not available
                                if (gpsSpeed === null) {
                                    const timeDiff = (Date.now() - new Date(prev.timestamp || Date.now()).getTime()) / 1000;
                                    if (timeDiff > 0) {
                                        setCurrentSpeed((delta / timeDiff) * 3600);
                                    }
                                }
                            }
                        }
                        return { lat: latitude, lng: longitude, timestamp: Date.now() };
                    });
                },
                (error) => {
                    toast.error('GPS tracking error. Please enable location services.');
                },
                { enableHighAccuracy: true, timeout: 5000, maximumAge: 0 }
            );
        }
    }, [mapLoaded]);

    const startRide = async () => {
        try {
            const res = await fetch(`${API}/rides/start`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ vehicle_type: vehicleType })
            });

            if (res.ok) {
                const ride = await res.json();
                setCurrentRide(ride);
                setIsRiding(true);
                setDistance(0);
                setDuration(0);
                setGpsTrace([]);
                startTimeRef.current = new Date();
                startTracking();
                toast.success('Ride started! Track your distance.');
            } else {
                const error = await res.json();
                toast.error(error.detail || 'Failed to start ride');
            }
        } catch (error) {
            toast.error('Failed to start ride');
        }
    };

    const stopRide = async () => {
        if (watchIdRef.current) {
            navigator.geolocation.clearWatch(watchIdRef.current);
            watchIdRef.current = null;
        }
        if (timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
        }

        if (!currentRide) {
            setIsRiding(false);
            return;
        }

        try {
            const res = await fetch(`${API}/rides/${currentRide.ride_id}/finish`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({
                    total_distance: distance,
                    gps_trace: gpsTrace
                })
            });

            if (res.ok) {
                const result = await res.json();
                setRideResult(result);
                setIsRiding(false);
                setCurrentSpeed(0);
                
                // Show celebration screen
                setShowCelebration(true);

                if (result.validation?.passed) {
                    toast.success(`Ride completed! Earned Boltz`);
                    updateUser({ boltz_balance: (user?.boltz_balance || 0) + result.boltzEarned });
                } else {
                    toast.warning('Ride flagged for validation. ' + (result.validation?.flags?.join(', ') || ''));
                }
            }
        } catch (error) {
            toast.error('Failed to finish ride');
        }

        setCurrentRide(null);
    };

    const formatDuration = (seconds) => {
        const hrs = Math.floor(seconds / 3600);
        const mins = Math.floor((seconds % 3600) / 60);
        const secs = seconds % 60;
        if (hrs > 0) {
            return `${hrs}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
        }
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    return (
        <Layout>
            <div className="max-w-2xl mx-auto px-4 sm:px-6 py-6">
                <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mb-6"
                >
                    <span className="label-text text-primary">RIDE TRACKING</span>
                    <h1 className="text-2xl font-bold mt-1">
                        {isRiding ? 'Ride in Progress' : 'Start Your Ride'}
                    </h1>
                </motion.div>

                {/* Map */}
                <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 }}
                    className="rounded-xl border border-border overflow-hidden mb-6 relative"
                    style={{ height: isRiding ? '420px' : '300px' }}
                >
                    <div ref={mapContainer} style={{ height: '100%', width: '100%' }} />
                    
                    {/* Speedometer overlay */}
                    {isRiding && (
                        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-10 pointer-events-none" data-testid="speedometer-overlay">
                            <div className="bg-black/70 backdrop-blur-lg rounded-2xl border border-white/10 p-1 shadow-[0_0_30px_rgba(0,0,0,0.5)]">
                                <Speedometer speed={currentSpeed} maxSpeed={80} />
                            </div>
                        </div>
                    )}

                    {/* GPS indicator */}
                    {isRiding && (
                        <div className="absolute top-4 right-4 flex items-center gap-2 bg-background/90 backdrop-blur px-3 py-1.5 rounded-full z-10">
                            <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                            <span className="text-xs font-medium">GPS Active</span>
                        </div>
                    )}
                </motion.div>

                {/* Stats Display */}
                <AnimatePresence mode="wait">
                    {isRiding ? (
                        <motion.div
                            key="riding"
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                            className="grid grid-cols-3 gap-4 mb-6"
                        >
                            <Card className="glass-card">
                                <CardContent className="p-4 text-center">
                                    <span className="label-text">DISTANCE</span>
                                    <p className="text-2xl font-black font-mono mt-1" data-testid="ride-distance">
                                        {distance.toFixed(2)}
                                        <span className="text-sm text-muted-foreground ml-1">km</span>
                                    </p>
                                </CardContent>
                            </Card>
                            <Card className="glass-card">
                                <CardContent className="p-4 text-center">
                                    <span className="label-text">DURATION</span>
                                    <p className="text-2xl font-black font-mono mt-1" data-testid="ride-duration">
                                        {formatDuration(duration)}
                                    </p>
                                </CardContent>
                            </Card>
                            <Card className="glass-card">
                                <CardContent className="p-4 text-center">
                                    <span className="label-text">SPEED</span>
                                    <p className="text-2xl font-black font-mono mt-1" data-testid="ride-speed">
                                        {duration > 0 ? ((distance / (duration / 3600)).toFixed(1)) : '0.0'}
                                        <span className="text-sm text-muted-foreground ml-1">km/h</span>
                                    </p>
                                </CardContent>
                            </Card>
                        </motion.div>
                    ) : (
                        <motion.div
                            key="not-riding"
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                            className="mb-6"
                        >
                            <Card className="glass-card">
                                <CardContent className="p-6">
                                    <label className="label-text block mb-3">SELECT VEHICLE</label>
                                    <Select value={vehicleType} onValueChange={setVehicleType}>
                                        <SelectTrigger className="w-full" data-testid="vehicle-select">
                                            <SelectValue placeholder="Select vehicle" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {VEHICLES.map((v) => (
                                                <SelectItem key={v.id} value={v.id}>
                                                    <div className="flex items-center justify-between w-full">
                                                        <span>{v.name}</span>
                                                    </div>
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                    <p className="text-sm text-muted-foreground mt-3 flex items-center">
                                        <Lightning size={16} className="mr-1 text-primary" />
                                        Electric vehicles earn 1.5x Boltz rewards
                                    </p>
                                </CardContent>
                            </Card>
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* Start/Stop Button */}
                <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                >
                    {isRiding ? (
                        <Button
                            onClick={stopRide}
                            size="lg"
                            className="w-full bg-destructive text-destructive-foreground font-bold text-lg py-8 rounded-2xl"
                            data-testid="stop-ride-button"
                        >
                            <Stop size={24} className="mr-2" weight="fill" />
                            Stop Ride
                        </Button>
                    ) : (
                        <Button
                            onClick={startRide}
                            size="lg"
                            className="w-full btn-primary text-lg py-8 rounded-2xl"
                            data-testid="start-ride-button"
                        >
                            <Play size={24} className="mr-2" weight="fill" />
                            Start Ride
                        </Button>
                    )}
                </motion.div>

                {/* Ride Result */}
                <AnimatePresence>
                    {rideResult && (
                        <motion.div
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.95 }}
                            className="mt-6"
                        >
                            <Card className={`glass-card ${rideResult.validation?.passed ? 'border-neon-green/30 box-glow' : 'border-neon-gold/30 box-glow-gold'}`}>
                                <CardContent className="p-6">
                                    <h3 className="font-bold text-lg mb-4 font-display text-foreground">
                                        {rideResult.validation?.passed ? 'Ride Completed!' : 'Ride Under Review'}
                                    </h3>
                                    <div className="grid grid-cols-2 gap-4 text-sm">
                                        <div>
                                            <span className="text-muted-foreground">Distance</span>
                                            <p className="font-bold text-foreground">{rideResult.ride?.distance?.toFixed(2)} km</p>
                                        </div>
                                        <div>
                                            <span className="text-muted-foreground">Boltz Earned</span>
                                            <p className="font-bold font-mono text-neon-gold text-glow-gold">
                                                +{rideResult.boltzEarned?.toFixed(2)} Boltz
                                            </p>
                                        </div>
                                        <div>
                                            <span className="text-muted-foreground">Avg Speed</span>
                                            <p className="font-bold text-foreground">{rideResult.validation?.averageSpeed} km/h</p>
                                        </div>
                                        <div>
                                            <span className="text-muted-foreground">Multiplier</span>
                                            <p className="font-bold text-primary">{rideResult.validation?.rewardMultiplier}x</p>
                                        </div>
                                    </div>
                                    <Button 
                                        className="w-full mt-4"
                                        variant="outline"
                                        onClick={() => setRideResult(null)}
                                    >
                                        Close
                                    </Button>
                                </CardContent>
                            </Card>
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* Tips */}
                <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                    className="mt-8"
                >
                    <Card className="glass-card bg-muted/30">
                        <CardContent className="p-4">
                            <h3 className="font-bold text-sm mb-2 flex items-center">
                                <Path size={16} className="mr-2 text-primary" />
                                Ride Tips
                            </h3>
                            <ul className="text-sm text-muted-foreground space-y-1">
                                <li>• Keep GPS enabled for accurate tracking</li>
                                <li>• Rides under 100m may not be validated</li>
                                <li>• Electric vehicles earn 50% more Boltz</li>
                            </ul>
                        </CardContent>
                    </Card>
                </motion.div>
            </div>

            {/* Ride Completion Celebration */}
            {showCelebration && rideResult && (
                <RideComplete
                    rideResult={rideResult}
                    vehicleType={vehicleType}
                    onClose={() => setShowCelebration(false)}
                />
            )}
        </Layout>
    );
}
